import { Transition } from "@headlessui/react"
import { Form, Head, usePage } from "@inertiajs/react"

import DeleteUser from "@/components/delete-user"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import DashboardLayout from "@/layouts/dashboard-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsProfilePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Profile settings",
    href: settingsProfilePath(),
  },
]

interface SharedProfileData {
  auth: {
    user: {
      name: string
      openrouter_api_key?: string | null
      openrouter_model_id?: string | null
      default_model: string
    }
  }
  available_models: string[]
  [key: string]: unknown
}

export default function Profile() {
  const { auth, available_models, default_model } =
    usePage<SharedProfileData>().props

  return (
    <DashboardLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Profile information"
            description="Update your name"
          />

          <Form
            method="patch"
            action={settingsProfilePath()}
            options={{
              preserveScroll: true,
            }}
            className="space-y-6"
          >
            {({ errors, processing, recentlySuccessful }) => (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    className="mt-1 block w-full"
                    defaultValue={auth.user.name}
                    required
                    autoComplete="name"
                    placeholder="Full name"
                  />
                  <InputError className="mt-2" message={errors.name} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="openrouter_api_key">API Key</Label>
                  <Input
                    id="openrouter_api_key"
                    name="openrouter_api_key"
                    className="mt-1 block w-full"
                    defaultValue={auth.user.openrouter_api_key ?? ""}
                    placeholder="Your OpenRouter API key"
                  />
                  <InputError
                    className="mt-2"
                    message={errors.openrouter_api_key}
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Model Name</Label>
                  <Select
                    name="openrouter_model_id"
                    defaultValue={
                      (auth.user.openrouter_model_id ?? default_model) as string
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Model Name" />
                    </SelectTrigger>
                    <SelectContent>
                      {available_models.map((model) => (
                        <SelectItem key={model} value={model}>
                          {model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <InputError
                    className="mt-2"
                    message={errors.openrouter_model_id}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Button disabled={processing}>Save</Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-sm text-neutral-600">Saved</p>
                  </Transition>
                </div>
              </>
            )}
          </Form>
        </div>

        <DeleteUser />
      </SettingsLayout>
    </DashboardLayout>
  )
}
